package er.prateek.crossly

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.LifecycleEventObserver
import androidx.lifecycle.compose.LocalLifecycleOwner
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavDestination.Companion.hasRoute
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import er.prateek.crossly.ui.achievements.AchievementCelebrationHost
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.warmUp
import er.prateek.crossly.share.ShareCodec
import er.prateek.crossly.share.ShareFunnel
import er.prateek.crossly.share.ShareOverlay
import er.prateek.crossly.share.browserEnvironment
import er.prateek.crossly.share.currentPageUrl
import er.prateek.crossly.share.isWebRuntime
import er.prateek.crossly.share.webClientFromUserAgent
import er.prateek.crossly.ui.gameplay.currentGameplayConfig
import er.prateek.crossly.ui.gameplay.rememberGameplayState
import er.prateek.crossly.ui.navigation.AppNavHost
import er.prateek.crossly.ui.navigation.Destination
import er.prateek.crossly.ui.share.ShareOverlayHost
import er.prateek.crossly.ui.share.WebShareLandingScreen
import er.prateek.crossly.ui.theme.AppTheme
import er.prateek.crossly.ui.theme.rememberThemeState

@Composable
fun App() {
    val pageUrl = remember { currentPageUrl() }
    val inbound = remember(pageUrl) { ShareCodec.parseShareUrl(pageUrl) }
    val phoneWeb = isWebRuntime() && webClientFromUserAgent(browserEnvironment().userAgent).isPhone
    // Built from the last-activated config; synced again below once this launch's fetch lands.
    val gameplayState = rememberGameplayState()

    // No-ops until a remote base URL is configured (see AppContainer); once one
    // is, this is what picks up newly published packs/days with no app release.
    LaunchedEffect(Unit) {
        AppContainer.contentRepository.refreshFromRemote()
    }

    LaunchedEffect(Unit) {
        AppContainer.analytics.setAnalyticsEnabled(AppContainer.analyticsPrefsStore.isEnabled())
        val sessionCount = AppContainer.profileStore.incrementAndGetSessionCount()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.APP_OPEN,
            mapOf(
                "platform" to getPlatform().name,
                "is_first_open" to (sessionCount == 1),
                "session_count" to sessionCount,
                "has_saved_session" to (AppContainer.savedSessionManager.session != null),
                "current_streak" to AppContainer.dailyPuzzleManager.currentStreak().currentStreak,
                "longest_streak" to AppContainer.dailyPuzzleManager.currentStreak().longestStreak,
                "play_streak" to AppContainer.streakManager.displayStreak(),
                "badges_unlocked" to AppContainer.achievementManager.unlockedCount(),
                "total_stars_earned" to AppContainer.progressionManager.totalStarsEarned(),
                "ad_free" to AppContainer.adsCoordinator.entitlement.value.adFree,
                "is_web" to isWebRuntime(),
            ),
        )
        // Seeds RemoteConfigDefaults and fetches+activates once; ProgressionManager
        // already falls back to the compiled default until/unless this completes.
        AppContainer.remoteConfig.warmUp()
        // Strictly after warmUp, same ordering rule as adsCoordinator.start() below: reads a
        // freshly-activated `notifications` config, not the compiled default.
        AppContainer.notificationCoordinator.onAppOpened()
        // Back-fills badges an existing player already earned (silently), then catches up any
        // unlock a previous session missed.
        AppContainer.playerJourney.onAppStarted()
        // A freshly fetched per-level `default` reaches players who never picked for that level.
        gameplayState.syncWithConfig(currentGameplayConfig())
        // Strictly after warmUp: this reads `app_config` from whatever has been activated,
        // so running it earlier would initialize the ad SDKs against compiled defaults and
        // ignore whatever the console currently says.
        AppContainer.adsCoordinator.start()
        AppContainer.savedSessionManager.dropStaleDaily(AppContainer.dailyPuzzleManager.todayIso())
        if (isWebRuntime() && inbound != null) {
            if (phoneWeb) {
                ShareFunnel.log(AppContainer.analytics, AnalyticsEvents.SHARE_WEB_OPEN, inbound.first)
            } else {
                AppContainer.shareManager.handleIncomingUrl(pageUrl, AnalyticsEvents.SHARE_WEB_OPEN)
            }
        }
        AppContainer.shareManager.onColdStart()
        AppContainer.shareManager.refreshOverlayWithLabels()
    }

    // App-process-level foreground/background, distinct from the per-screen pause/resume
    // CrosswordScreen already tracks for its own timer -- this is "did the player leave the
    // app entirely" (switched apps, locked the phone), useful for session-length and
    // re-engagement analysis regardless of which screen they were on.
    val lifecycleOwner = LocalLifecycleOwner.current
    DisposableEffect(lifecycleOwner) {
        val observer = LifecycleEventObserver { _, event ->
            when (event) {
                Lifecycle.Event.ON_STOP -> AppContainer.analytics.logEvent(AnalyticsEvents.APP_BACKGROUND)
                Lifecycle.Event.ON_START -> AppContainer.analytics.logEvent(AnalyticsEvents.APP_FOREGROUND)
                else -> Unit
            }
        }
        lifecycleOwner.lifecycle.addObserver(observer)
        onDispose { lifecycleOwner.lifecycle.removeObserver(observer) }
    }

    val themeState = rememberThemeState()
    AppTheme(themeState = themeState) {
        if (phoneWeb && inbound != null) {
            WebShareLandingScreen(payload = inbound.first, token = inbound.second)
            return@AppTheme
        }
        val navController = rememberNavController()
        val shareUi by AppContainer.shareManager.ui.collectAsStateWithLifecycle()
        LaunchedEffect(shareUi.openPuzzle) {
            val open = shareUi.openPuzzle ?: return@LaunchedEffect
            val riddle = open.riddle
            if (riddle != null) {
                navController.navigate(
                    Destination.RiddleRun(
                        levelId = riddle.levelId,
                        seed = riddle.seed,
                        fromChallenge = true,
                        challengerName = open.challengerName,
                        challengerTimeMs = open.challengerTimeMs,
                        challengerScore = open.challengerScore,
                    ),
                )
                AppContainer.shareManager.clearOpenPuzzle()
                return@LaunchedEffect
            }
            navController.navigate(
                Destination.Crossword(
                    puzzleId = open.puzzleId,
                    fromChallenge = true,
                    challengerName = open.challengerName,
                    challengerTimeMs = open.challengerTimeMs,
                ),
            )
            AppContainer.shareManager.clearOpenPuzzle()
        }
        LaunchedEffect(shareUi.openLevels) {
            if (!shareUi.openLevels) return@LaunchedEffect
            navController.navigate(Destination.LevelSelect())
            AppContainer.shareManager.clearOpenLevels()
        }
        // Badge celebrations wait while a board is live -- a pop-up over a timed run would cost
        // the player the run. They play on the next results/break/home screen instead.
        val currentEntry by navController.currentBackStackEntryAsState()
        val onBoard = currentEntry?.destination?.let { dest ->
            dest.hasRoute<Destination.Crossword>() ||
                dest.hasRoute<Destination.Daily>() ||
                dest.hasRoute<Destination.MathCrossword>() ||
                dest.hasRoute<Destination.RiddleRun>()
        } ?: true
        Box {
            AppNavHost(
                navController = navController,
                themeState = themeState,
                gameplayState = gameplayState,
            )
            ShareOverlayHost()
            AchievementCelebrationHost(canPresent = !onBoard && shareUi.overlay == ShareOverlay.None)
        }
    }
}
